// src/pages/dashboard/branch/variant-management/VariantManagementPage.tsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { FiPlus, FiEdit3, FiTrash2, FiSettings, FiPackage, FiTrendingUp, FiSearch, FiFilter, FiX } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../../../../components/ui/Button';
import {
  BranchActionConfig,
  ActionType,
  getActionTypes,
  getAllBranchActionsByBranchId,
  insertBranchActionConfig,
  updateBranchActionConfig,
  deleteBranchActionConfigById,
  updateItemVariantById,
} from '../../../../utils/supabaseClient';

import ConfirmDeleteModal from '../../../../components/ui/ConfirmDeleteModal';
import VariantConfigModal from './components/VariantConfigModal';
import i18n from '../../../../i18n/config';

const VariantManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const { branchId } = useParams<{ branchId: string }>();

  const [actionTypes, setActionTypes] = useState<ActionType[]>([]);
  const [branchActions, setBranchActions] = useState<BranchActionConfig[]>([]);
  const [filteredActions, setFilteredActions] = useState<BranchActionConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<BranchActionConfig | null>(null);
  const [deleteConfig, setDeleteConfig] = useState<BranchActionConfig | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedActionType, setSelectedActionType] = useState<string>('all');

  // Test/Demo override functionality
  const [testItemId, setTestItemId] = useState<string>('');
  const [testVariantId, setTestVariantId] = useState<string>('');
  const [testOverrideData, setTestOverrideData] = useState<any>(null);
  const [showOverrideTest, setShowOverrideTest] = useState(false);

  useEffect(() => {
    if (branchId) {
      loadData();
    }
  }, [branchId]);

  // Filter effects
  useEffect(() => {
    let filtered = branchActions;

    // Apply search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(action =>
        action.form_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        action.action_type.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply action type filter
    if (selectedActionType !== 'all') {
      filtered = filtered.filter(action =>
        action.action_type.toLowerCase() === selectedActionType.toLowerCase()
      );
    }

    setFilteredActions(filtered);
  }, [branchActions, searchQuery, selectedActionType]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [actionTypesResult, branchActionsResult] = await Promise.all([
        getActionTypes(),
        getAllBranchActionsByBranchId(branchId!),
      ]);

      if (actionTypesResult.data) {
        setActionTypes(actionTypesResult.data);
      }

      if (branchActionsResult.data) {
        setBranchActions(branchActionsResult.data);
      }
    } catch (error) {
      console.error('Error loading variant data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingConfig(null);
    setIsModalOpen(true);
  };

  const handleEdit = (config: BranchActionConfig) => {
    setEditingConfig(config);
    setIsModalOpen(true);
  };

  const handleEditWithOverride = (config: BranchActionConfig) => {
    setEditingConfig(config);
    setTestItemId('demo-item-123'); // Demo item ID
    const existingVariantId = (config as unknown as { item_variant_id?: string })?.item_variant_id;
    setTestVariantId(existingVariantId || '');

    // Pre-populate override data with the base configuration so the modal opens in override mode
    if (Array.isArray(config.data)) {
      setTestOverrideData(
        config.data.map((option: any) => ({
          name: option.name || '',
          note: option.note || '',
          price: option.price ?? 0,
          is_active: option.is_active !== undefined ? option.is_active : true,
          is_default: option.is_default || false,
          description: option.description || '',
        }))
      );
    } else {
      setTestOverrideData([]);
    }

    setShowOverrideTest(true);
    setIsModalOpen(true);
  };

  interface OverrideSavePayload {
    variantId?: string;
    itemId: string;
    branchActionConfigId: string;
    overrideData: any;
    actionType: string;
  }

  const handleSaveOverride = async ({
    variantId,
    itemId: overrideItemId,
    branchActionConfigId,
    overrideData,
  }: OverrideSavePayload) => {
    setTestOverrideData(overrideData);

    if (!variantId) {
      console.warn('No item variant ID provided for override update. Skipping Supabase RPC call.');
    } else {
      try {
        const result = await updateItemVariantById({
          id: variantId,
          item_id: overrideItemId,
          branch_action_config_id: branchActionConfigId,
          override_data: overrideData,
        });

        if (!result.data) {
          console.warn('update_item_variant_by_id did not return data. Inspect Supabase response for details.');
        }

        // Refresh local state so UI reflects latest data
        await loadData();
      } catch (error) {
        console.error('Error updating item variant override:', error);
      }
    }

    setIsModalOpen(false);
    setEditingConfig(null);
    setTestItemId('');
    setTestVariantId('');
    setShowOverrideTest(false);
  };

  const handleDelete = async (config: BranchActionConfig) => {
    try {
      await deleteBranchActionConfigById(config.id);
      setBranchActions(prev => prev.filter(c => c.id !== config.id));
      setDeleteConfig(null);
    } catch (error) {
      console.error('Error deleting variant config:', error);
    }
  };

  const handleSave = async (configData: Omit<BranchActionConfig, 'id'>) => {
    try {
      if (editingConfig) {
        // Update existing
        const fullConfig: BranchActionConfig = {
          ...configData,
          id: editingConfig.id,
        };
        const result = await updateBranchActionConfig(fullConfig);
        if (result.data) {
          setBranchActions(prev =>
            prev.map(c => c.id === editingConfig.id ? result.data! : c)
          );
        }
        await loadData();
      } else {
        // Create new
        const result = await insertBranchActionConfig(configData);
        if (result.data) {
          setBranchActions(prev => [...prev, result.data!]);
        }
        await loadData();
      }
      setIsModalOpen(false);
      setEditingConfig(null);
      setTestItemId('');
      setShowOverrideTest(false);
    } catch (error) {
      console.error('Error saving variant config:', error);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedActionType('all');
  };

  const getActionTypeIcon = (actionType: string) => {
    switch (actionType.toLowerCase()) {
      case 'selection':
        return <FiSettings className="w-5 h-5" />;
      case 'upgrade':
        return <FiTrendingUp className="w-5 h-5" />;
      default:
        return <FiPackage className="w-5 h-5" />;
    }
  };

  const getActionTypeColor = (actionType: string) => {
    switch (actionType.toLowerCase()) {
      case 'selection':
        return 'blue';
      case 'upgrade':
        return 'purple';
      default:
        return 'orange';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <motion.div
          className="animate-pulse space-y-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-1/4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <motion.div
                key={i}
                className="h-20 bg-gradient-to-r from-gray-200 to-gray-300 rounded"
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                transition={{ delay: i * 0.1 }}
              ></motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    );
  }
  const currentLanguage = i18n.language;
  return (
    <motion.div
      className="p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header Section */}
      <motion.div
        className="mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.5 }}
      >
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              {t('common.variants.management.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {t('common.variants.management.description')}
            </p>
            <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                {branchActions.length} {t('common.variants.management.totalVariants')}
              </span>
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                {filteredActions.length} {t('common.variants.management.showing')}
              </span>
            </div>
          </div>
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center"
          >
            <Button
              onClick={handleCreate}
              leftIcon={<FiPlus className="w-4 h-4" />}
              className="hover:from-blue-700 hover:to-purple-700 shadow-lg"
            >
              {t('common.variants.management.addVariant')}
            </Button>
          </motion.div>
        </div>
      </motion.div>

      {/* Search and Filter Section */}
      <motion.div
        className="mb-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-5"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        <div className="flex flex-col gap-4">
          {/* Search Input with Clear Button */}
          <div className="relative">
            <FiSearch className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={t('common.variants.management.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors"
              >
                <FiX className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filter Pills Row */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mr-1">
              <FiFilter className="w-3.5 h-3.5 inline-block mr-1" />
              {t('common.filter')}:
            </span>
            
            {/* All Types Pill */}
            <button
              onClick={() => setSelectedActionType('all')}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${
                selectedActionType === 'all'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 ring-1 ring-blue-300 dark:ring-blue-700'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {t('common.variants.management.allTypes')}
            </button>
            
            {/* Action Type Pills */}
            {actionTypes.map((type) => {
              const isSelected = selectedActionType.toLowerCase() === type.name.toLowerCase();
              const color = type.name.toLowerCase() === 'selection' ? 'blue' : type.name.toLowerCase() === 'upgrade' ? 'purple' : 'orange';
              
              return (
                <button
                  key={type.name}
                  onClick={() => setSelectedActionType(type.name)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${
                    isSelected
                      ? color === 'blue'
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 ring-1 ring-blue-300 dark:ring-blue-700'
                        : color === 'purple'
                        ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300 ring-1 ring-purple-300 dark:ring-purple-700'
                        : 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300 ring-1 ring-orange-300 dark:ring-orange-700'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {type.name}
                </button>
              );
            })}

            {/* Clear Filters - Only show when filters are active */}
            {(searchQuery || selectedActionType !== 'all') && (
              <motion.button
                onClick={clearFilters}
                className="ml-auto px-3 py-1.5 text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 font-medium rounded-full transition-colors"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('common.clearFilters')}
              </motion.button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Content Section */}
      {filteredActions.length === 0 ? (
        <motion.div
          className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
          >
            <div className="w-20 h-20 mx-auto mb-6 bg-blue-100 dark:bg-blue-900/40 rounded-full flex items-center justify-center">
              <FiSettings className="w-10 h-10 text-blue-600 dark:text-blue-400" />
            </div>
          </motion.div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
            {branchActions.length === 0 ? t('common.variants.management.noVariants') : t('common.variants.management.noResults')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
            {branchActions.length === 0
              ? t('common.variants.management.noVariantsDescription')
              : t('common.variants.management.noResultsDescription')
            }
          </p>
          {branchActions.length === 0 ? (
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                onClick={handleCreate}
                leftIcon={<FiPlus className="w-4 h-4" />}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {t('common.variants.management.createFirst')}
              </Button>
            </motion.div>
          ) : (
            <motion.button
              onClick={clearFilters}
              className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t('common.clearFilters')}
            </motion.button>
          )}
        </motion.div>
      ) : (
        <motion.div
          className="grid gap-5 sm:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <AnimatePresence mode="popLayout">
            {filteredActions.map((config) => {
              const color = getActionTypeColor(config.action_type);
              const accentColorClass = color === 'blue' ? 'bg-blue-500' : color === 'purple' ? 'bg-purple-500' : 'bg-orange-500';
              const iconBgClass = color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400' : color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400' : 'bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400';
              const badgeBgClass = color === 'blue' ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : color === 'purple' ? 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300';
              
              return (
                <motion.div
                  key={config.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{
                    layout: { duration: 0.3, type: "spring", stiffness: 300, damping: 30 },
                    opacity: { duration: 0.2 },
                    scale: { duration: 0.2 }
                  }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className="group bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 relative overflow-hidden border border-gray-200 dark:border-gray-700"
                >
                  {/* Top accent bar - solid color, always visible */}
                  <div className={`absolute top-0 left-0 right-0 h-1.5 ${accentColorClass}`}></div>

                  {/* Card Content */}
                  <div className="p-5 pt-6">
                    {/* Header Row */}
                    <div className="flex items-start justify-between gap-3 mb-5">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`p-2.5 rounded-xl ${iconBgClass} flex-shrink-0`}>
                          {getActionTypeIcon(config.action_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 dark:text-white text-base leading-tight truncate mb-1.5">
                            {currentLanguage === 'ar' && config.ar && typeof config.ar === 'object' && !Array.isArray(config.ar) && (config.ar as any).form_name
                              ? (config.ar as any).form_name
                              : config.form_name}
                          </h3>
                          <span className={`inline-block text-xs px-2.5 py-0.5 rounded-full ${badgeBgClass} font-medium`}>
                            {config.action_type}
                          </span>
                        </div>
                      </div>
                      
                      {/* Action buttons - visible on mobile, hover on desktop */}
                      <div className="flex gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200 flex-shrink-0">
                        <motion.button
                          onClick={() => handleEdit(config)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-all duration-200"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                          title="Edit Configuration"
                        >
                          <FiEdit3 className="w-4 h-4" />
                        </motion.button>
                        <motion.button
                          onClick={() => setDeleteConfig(config)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all duration-200"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </motion.button>
                      </div>
                    </div>

                    {/* Stats Row - Horizontal Pills */}
                    <div className="flex flex-wrap gap-2">
                      {/* Required indicator */}
                      <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 dark:bg-gray-700/60 rounded-lg">
                        <div className={`w-2 h-2 rounded-full ${config.is_required ? 'bg-red-500' : 'bg-gray-400'}`}></div>
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-300">{t('common.variants.required')}</span>
                      </div>
                      
                      {/* Multiselection indicator */}
                      <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 dark:bg-gray-700/60 rounded-lg">
                        <div className={`w-2 h-2 rounded-full ${config.is_multiselection ? 'bg-blue-500' : 'bg-gray-400'}`}></div>
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-300">{t('common.variants.multiselection')}</span>
                      </div>
                      
                      {/* Selection Range */}
                      <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 dark:bg-gray-700/60 rounded-lg">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-300">{config.min_selection}-{config.max_selection}</span>
                      </div>
                      
                      {/* Options Count */}
                      {config.data && (
                        <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 dark:bg-gray-700/60 rounded-lg">
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                            {Array.isArray(config.data) ? `${config.data.length} ${t('common.variants.management.options')}` : t('common.variants.comboData')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Create/Edit Modal */}
      <VariantConfigModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setTestItemId('');
          setTestVariantId('');
          setShowOverrideTest(false);
        }}
        onSave={handleSave}
        config={editingConfig}
        actionTypes={actionTypes}
        branchId={branchId!}
        itemId={showOverrideTest ? testItemId : undefined}
        itemVariantId={showOverrideTest ? testVariantId : undefined}
        overrideData={showOverrideTest ? testOverrideData : undefined}
        onSaveOverride={showOverrideTest ? handleSaveOverride : undefined}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={!!deleteConfig}
        onCancel={() => setDeleteConfig(null)}
        onConfirm={() => deleteConfig && handleDelete(deleteConfig)}
        title={t('common.variants.management.deleteTitle')}
        message={t('common.variants.management.deleteDescription', { name: deleteConfig?.form_name })}
      />
    </motion.div>
  );
};

export default VariantManagementPage;